import { Injectable, Inject, NgZone, DOCUMENT } from '@angular/core';


@Injectable({
  providedIn: 'root'
})
export class ExternalAccessibilityObserverService {
  private observer!: MutationObserver;
  private landmarkCounter = 0;
  private processTimeout: any;
  private started = false;
  private globalLandmarkLabels = new Map<string, number>();

  constructor(@Inject(DOCUMENT) private document: Document) { }

  public start(): void {
    // Guard: to run only once
    if (this.started) return;

    this.started = true;
    const body = this.document.body as HTMLElement;

    // Apply fixes to the current DOM which is already loaded.
    this.applyFixesToTree(body);

     // Begin watching for future DOM changes to aplly fixes.
    this.initObserver();
  }

  private applyFixesToTree(root: HTMLElement): void {
    this.applyWcagFixes(root);
    root.querySelectorAll('*').forEach((el) => {
      if (el instanceof HTMLElement) {
        this.applyWcagFixes(el);
      }
    });
  }

  // Disconnect the observer to stop receiving mutations
  public stop(): void {
    if (this.observer) {
      try {
        this.observer.disconnect();
      } finally {
        this.observer = undefined;
      }
    }

    // Clear debounced work
    if (this.processTimeout) {
      clearTimeout(this.processTimeout);
      this.processTimeout = undefined;
    }

    // Reset started flag
    this.started = false;
  }


  // this will catch and fix elements added or changed after initial load
  private initObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      let nodesAdded = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.applyFixesToTree(node);
              //Set it true so a follow-up pass can run later
              nodesAdded = true;
            }
          });
        }
        else if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement && mutation.attributeName) {
          this.handleAttributeMutation(mutation.target, mutation.attributeName);
        }
      }

      // If nodes were added, refresh a 300ms debounce
      if (nodesAdded) {
        clearTimeout(this.processTimeout);
        this.processTimeout = setTimeout(() => {
          this.ensureUniqueLandmarks(this.document.body);
        }, 300);
      }
    });

    this.observer.observe(this.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-valuenow', 'aria-activedescendant', 'aria-controls', 'aria-level', 'aria-expanded']
    });
  }

  private handleAttributeMutation(target: HTMLElement, attr: string) {
    switch (attr) {
      case 'aria-valuenow':
        this.fixInvalidAriaValues(target);
        break;

      case 'aria-activedescendant':
        this.fixInvalidAriaActivedescendant(target);
        break;

      case 'aria-controls':
        this.fixInvalidAriaControls(target);
        break;

      case 'aria-level':
        const role = target.getAttribute('role') || '';
        if (role === 'menuitem' && target.hasAttribute('aria-level')) {
          target.removeAttribute('aria-level');
        }
        break;

      case 'aria-expanded':
        this.fixInvalidAriaExpanded(target);
        break;
    }
  }

  private applyWcagFixes(element: HTMLElement): void {
    this.fixInteractiveElementsWithoutLabel(element);
    this.fixPrimeNGComponents(element);
    this.fixPrimeNGDropdownRoles(element);
    this.fixAriaLabelOnGenericElements(element);
    this.fixListboxAccessibleNames(element);
    this.fixNestedInteractiveControls(element);
    this.fixInvalidAriaRequired(element);
    this.fixScrollableContainers(element);
    this.fixTableHeadersAccessibility(element);
    this.fixMissingLandmarks(element);
    this.normalizeRowFocusTarget(element);
  }

  private normalizeRowFocusTarget(root: HTMLElement): void {
    root.querySelectorAll<HTMLElement>('tr.p-selectable-row[tabindex="0"]').forEach(tr => {
      const firstCell = tr.querySelector<HTMLElement>('td, th');
      if (!firstCell) return;
      tr.setAttribute('tabindex', '-1');
      if (!firstCell.hasAttribute('tabindex')) {
        firstCell.setAttribute('tabindex', '0');
      }
    });
  }

  // fix PrimeNG dropdowns
  private fixPrimeNGDropdownRoles(element: HTMLElement): void {
    const wrappers = element.querySelectorAll('.p-dropdown-items-wrapper');

    wrappers.forEach(wrapper => {
      if (wrapper.getAttribute('role') === 'listbox') {
        wrapper.removeAttribute('role');
        wrapper.setAttribute('role', 'presentation');
      }

      if (!wrapper.hasAttribute('tabindex')) {
        wrapper.setAttribute('tabindex', '0');
      }

      const ul = wrapper.querySelector('ul');
      if (!ul) return;

      if (!ul.hasAttribute('role')) {
        ul.setAttribute('role', 'listbox');
      }

      const liElements = Array.from(ul.querySelectorAll<HTMLLIElement>('li'));

      liElements.forEach(li => {
        if (!li.hasAttribute('role')) {
          li.setAttribute('role', 'option');
        }
      });
    });
  }

  // fix for PrimeNG accessible name, message, role normalization
  private fixListboxAccessibleNames(element: HTMLElement): void {

    //ensure proper role and accessible name
    const listboxes = element.querySelectorAll('[role="listbox"], ul.p-listbox-list');
    listboxes.forEach(listboxEl => {
      const listbox = listboxEl as HTMLElement;

      if (listbox.tagName === 'UL' && listbox.classList.contains('p-listbox-list') && !listbox.hasAttribute('role')) {
        listbox.setAttribute('role', 'listbox');
      }

      const hasAriaLabel = listbox.hasAttribute('aria-label') && (listbox.getAttribute('aria-label') || '').trim() !== '';
      const hasAriaLabelledBy = listbox.hasAttribute('aria-labelledby') && (listbox.getAttribute('aria-labelledby') || '').trim() !== '';
      if (!hasAriaLabel && !hasAriaLabelledBy) {
        listbox.setAttribute('aria-label', 'Selectable list of options');
      }

      //iterate all list items and enforce roles/states
      const liItems = listbox.querySelectorAll('li');
      liItems.forEach(liEl => {
        const li = liEl as HTMLElement;
        const isGroup = li.classList.contains('p-multiselect-item-group');

        if (isGroup && li.getAttribute('role') !== 'presentation') {
          li.setAttribute('role', 'presentation');
          return;
        }

        const isEmptyMsg = li.classList.contains('p-listbox-empty-message');

        if (isEmptyMsg) {
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');
          li.setAttribute('aria-disabled', 'true');
          li.removeAttribute('tabindex');
          li.removeAttribute('aria-live');
          li.removeAttribute('aria-hidden');

          const hasText = ((li.textContent || '').trim() !== '');
          const hasLabel = li.hasAttribute('aria-label') && (li.getAttribute('aria-label') || '').trim() !== '';
          const hasTitle = li.hasAttribute('title') && (li.getAttribute('title') || '').trim() !== '';

          if (!hasText && !hasLabel && !hasTitle) {
            li.setAttribute('aria-label', 'No options available');
            li.setAttribute('title', 'No options available');
          }
          return;
        }

        //enforce role and normalize aria-selected
        if (li.getAttribute('role') !== 'option')
          li.setAttribute('role', 'option');

        if (li.hasAttribute('aria-selected')) {
          const v = ((li.getAttribute('aria-selected') || '').toLowerCase());
          if (v !== 'true' && v !== 'false') li.setAttribute('aria-selected', 'false');
        } else {
          const isSelected = li.classList.contains('p-highlight');
          li.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        }
      });
    });
  }

  // fix for interactive elements (buttons, spans with role="button", links) to have an accessible name.
  private fixInteractiveElementsWithoutLabel(element: HTMLElement): void {

    const interactiveElements = element.querySelectorAll('button, [role="button"], a[role="button"], span[role="button"]');

    interactiveElements.forEach(el => {

      const htmlEl = el as HTMLElement;

      const hasText = !!htmlEl.textContent?.trim();
      const hasAriaLabel = htmlEl.hasAttribute('aria-label') && !!htmlEl.getAttribute('aria-label')?.trim();
      const hasAriaLabelledBy = htmlEl.hasAttribute('aria-labelledby') && !!htmlEl.getAttribute('aria-labelledby')?.trim();
      const hasTitle = htmlEl.hasAttribute('title') && !!htmlEl.getAttribute('title')?.trim();

      if (hasText || hasAriaLabel || hasAriaLabelledBy || hasTitle) return;

      const inferredRaw =
        htmlEl.getAttribute('data-label')?.trim() ||
        htmlEl.getAttribute('aria-description')?.trim() ||
        htmlEl.closest('.field, .form-group')?.querySelector('label')?.textContent?.trim() ||
        htmlEl.getAttribute('label')?.trim() ||
        htmlEl.getAttribute('name')?.trim() ||
        '';

      const inferred = this.getLabelText(inferredRaw || '', 'Perform Action');

      htmlEl.setAttribute('aria-label', inferred);
    });
  }

  // normalize aria-valuenow usage
  private fixInvalidAriaValues(el: HTMLElement): void {

    if(!el.hasAttribute('aria-valuenow')) return;

    const role = el.getAttribute('role') || '';
    const allowedRoles = ['spinbutton', 'slider', 'progressbar'];

    // remove aria-valuenow if applied to an unsupported role
    if (!allowedRoles.includes(role)) {
      el.removeAttribute('aria-valuenow');
      return;
    }

    // derive from input value or default to '0'
    let val = el.getAttribute('aria-valuenow');
    if (val === 'null' || val === '') {
      val = (el as HTMLInputElement).value || '0';

      let numericVal = Number(val);
      if (isNaN(numericVal)) {
        numericVal = 0;
      }

      el.setAttribute('aria-valuenow', String(numericVal));
    }

  }

  // validate aria-activedescendant
  private fixInvalidAriaActivedescendant(el: HTMLElement): void {
    if (el.hasAttribute('aria-activedescendant')) {

      const targetId = el.getAttribute('aria-activedescendant')!;
      if (!this.document.getElementById(targetId)) {
        el.removeAttribute('aria-activedescendant');
      }
    }
  }

  // validate aria-controls references
  private fixInvalidAriaControls(el: HTMLElement): void {
    if (el.hasAttribute('aria-controls')) {

      const targetId = el.getAttribute('aria-controls')!;

      if (targetId.startsWith('listbox-fallback-')) return;

      if (!this.document.getElementById(targetId)) {
        el.removeAttribute('aria-controls');
      }
    }
  }

  // resolve nested interactive controls, fix scenarios where interactive elements are improperly nested
  private fixNestedInteractiveControls(el: HTMLElement): void {

    const anchorsWithRoleButton = el.querySelectorAll('a[role="button"]');
    anchorsWithRoleButton.forEach(anchor => {
      const innerBtn = anchor.querySelector('button');

      if (innerBtn) {
        anchor.removeAttribute('role');
        anchor.removeAttribute('tabindex');
        anchor.removeAttribute('aria-expanded');
        anchor.removeAttribute('aria-controls');

        const hasLabel = innerBtn.hasAttribute('aria-label') && innerBtn.getAttribute('aria-label')!.trim() !== '';
        const hasTitle = innerBtn.hasAttribute('title') && innerBtn.getAttribute('title')!.trim() !== '';

        if (!hasLabel && !hasTitle) {
          const name = innerBtn.getAttribute('label') || innerBtn.textContent || 'action';
          innerBtn.setAttribute('aria-label', name.trim());
        }
      }
    });

    if (el.tagName === 'LI' && el.getAttribute('role') === 'option') {
      const nestedInteractive = el.querySelectorAll('button, [role="button"], a[role="button"]');
      if (nestedInteractive.length > 0) {
        el.removeAttribute('role');
        el.removeAttribute('tabindex');
        el.removeAttribute('aria-selected');
        el.removeAttribute('aria-label');
        el.removeAttribute('aria-labelledby');

        /// ensure each nested control has a name
        nestedInteractive.forEach(control => {
          const hasLabel = control.hasAttribute('aria-label') && control.getAttribute('aria-label')!.trim() !== '';
          const hasTitle = control.hasAttribute('title') && control.getAttribute('title')!.trim() !== '';
          if (!hasLabel && !hasTitle) {
            control.setAttribute('aria-label', 'action');
          }
        });

        const parentUl = el.closest('ul[role="listbox"]');
        if (parentUl) {
          parentUl.removeAttribute('role');
          parentUl.removeAttribute('aria-label');
          parentUl.removeAttribute('title');
        }
      }
    }
  }

  // ensure column headers have an accessible names
  private fixTableHeadersAccessibility(element: HTMLElement): void {
    const headers = element.querySelectorAll('th[scope="col"]');

    headers.forEach(th => {
      const text = th.textContent?.trim();
      const ariaLabel = th.getAttribute('aria-label')?.trim();

      if (!text && !ariaLabel) {
        const span = document.createElement('span');
        span.className = 'sr-only';
        span.textContent = 'Column';
        th.appendChild(span);
      } else if (!text && ariaLabel) {
        const span = document.createElement('span');
        span.className = 'sr-only';
        span.textContent = ariaLabel;
        th.appendChild(span);
      }
    });
  }

  // normalize non‑native button semantics , ensures clickable <div>s are accessible
  private fixAriaLabelOnGenericElements(el: HTMLElement): void {
    if (el.tagName === 'DIV' && el.hasAttribute('aria-label')) {
      const hasClick = typeof (el as any).onclick === 'function' || el.getAttribute('onclick') !== null;

      const isIconButtonLike =
        el.classList.contains('custom-icon') ||
        el.classList.contains('p-accordion-toggle-icon') ||
        el.getAttribute('data-test-id')?.includes('expand');

      if (hasClick || isIconButtonLike) {
        if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');

        el.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Enter') {
            (el as HTMLElement).click();
            event.preventDefault();
          }
        });

        const label = el.getAttribute('aria-label')?.trim() || 'Expand';
        el.setAttribute('aria-label', label);

      } else {
        //if not interactive,remove misleading label and hide
        el.removeAttribute('aria-label');
        el.setAttribute('aria-hidden', 'true');
      }
    }
  }

  // validate aria-required usage
  private fixInvalidAriaRequired(el: HTMLElement): void {
    if (el.hasAttribute('aria-required')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['radiogroup', 'combobox', 'textbox', 'listbox'];

      if (!allowedRoles.includes(role)) {
        const hasRadioButtons = el.querySelector('input[type="radio"]') !== null;
        if (hasRadioButtons) {
          el.setAttribute('role', 'radiogroup');
        } else {
          el.removeAttribute('aria-required');
        }
      }
    }
  }

  // validate aria-expanded usage
  private fixInvalidAriaExpanded(el: HTMLElement): void {
    if (el.hasAttribute('aria-expanded')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['button', 'link', 'combobox', 'menuitem', 'treeitem'];

      if (!allowedRoles.includes(role)) {
        el.removeAttribute('aria-expanded');
      }
    }
  }

  // fix scrollables
  private fixScrollableContainers(el: HTMLElement): void {
    const isScrollable = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;

    if (isScrollable && (el.classList.contains('p-datatable-wrapper') || el.classList.contains('ng-sidebar--left'))) {
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }

      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'region');
      }

      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        const baseLabel = el.classList.contains('p-datatable-wrapper')
          ? 'Scrollable data table'
          : 'Scrollable sidebar';

        this.landmarkCounter++;
        el.setAttribute('aria-label', `${baseLabel} ${this.landmarkCounter}`);
      }
    }
  }

  // ensure landmarks
  private fixMissingLandmarks(el: HTMLElement): void {
    // if element is in landmark,, skip
    const inLandmark = el.closest(
      'main, [role="main"], header, [role="banner"], footer, [role="contentinfo"], nav, [role="navigation"], aside, [role="complementary"], [role="region"], section[role="region"]'
    );
    if (inLandmark) return;

    const cls = el.classList;
    const isOverlayOrToast = cls.contains('p-toast') || cls.contains('p-toast-center') || cls.contains('p-component-overlay') || cls.contains('p-dialog-mask') || cls.contains('p-dialog-mask-scrollblocker')

    if (isOverlayOrToast) return;

    const tag = el.tagName.toLowerCase();
    const isContainer = tag === 'div' || tag === 'section' || tag === 'form';
    if (!isContainer) return;

    // using an immediate heading as name.
    const heading = el.querySelector(':scope h1, :scope h2, :scope h3, :scope h4, :scope h5, :scope h6') as HTMLElement | null;

    if (heading && (heading.textContent || '').trim() !== '') {
      if (!heading.id) {
        heading.id = `lm-h-${Math.random().toString(36).slice(2, 8)}`;
      }
      el.setAttribute('aria-labelledby', heading.id);
      el.removeAttribute('aria-label');
    } else {
      // default label
      const fallback = el.getAttribute('aria-label')?.trim() || 'Section';
      el.setAttribute('aria-label', fallback);
      el.removeAttribute('aria-labelledby');
    }

    if (tag === 'form') {
      if (!el.hasAttribute('role')) el.setAttribute('role', 'form');
    } else {
      el.setAttribute('role', 'region');
    }
  }

  // enforce unique landmark labels
  private ensureUniqueLandmarks(root: HTMLElement): void {
    // reset map
    this.globalLandmarkLabels.clear();

    const landmarks = root.querySelectorAll<HTMLElement>(
      '[role="region"], [role="main"], [role="navigation"], [role="banner"], [role="contentinfo"]'
    );

    const singularRoles = new Set(['main', 'banner', 'contentinfo']);

    landmarks.forEach((el) => {
      const role = el.getAttribute('role') || '';
      let label: string | null = null;

      // aria-labelledby text
      const labelledById = el.getAttribute('aria-labelledby');
      if (labelledById) {
        const headerEl = document.getElementById(labelledById);
        const headerText = headerEl?.textContent?.trim();
        if (headerText) {
          label = headerText; // Keep original case
        }
      }

      // if no label, use aria-label
      if (!label) {
        label = el.getAttribute('aria-label')?.trim() || '';
      }

      // defaults
      if (!label) {
        if (role === 'navigation') {
          label = el.closest('header') ? 'Primary Navigation'
                : el.closest('footer') ? 'Footer Navigation'
                : el.closest('aside') ? 'Sidebar Navigation'
                : 'Navigation';
        } else if (role === 'banner') {
          label = 'Header';
        } else if (role === 'contentinfo') {
          label = 'Footer';
        } else if (role === 'main') {
          label = 'Main';
        } else {
          label = 'Section';
        }
      }

      // for warning
      if (singularRoles.has(role)) {
        const count = (this.globalLandmarkLabels.get(role) || 0) + 1;
        this.globalLandmarkLabels.set(role, count);
        if (count > 1) {
          console.warn(`[a11y] Multiple "${role}" landmarks found.`);
        }
      }

      // ensure uniqueness for repeated labels
      const key = label.toLowerCase();
      if (this.globalLandmarkLabels.has(key)) {
        const count = (this.globalLandmarkLabels.get(key) || 0) + 1;
        this.globalLandmarkLabels.set(key, count);
        label = `${label} (${count})`;
      } else {
        this.globalLandmarkLabels.set(key, 1);
      }

      // applying label only if aria-labelledby was NOT valid
      if (!labelledById) {
        el.setAttribute('aria-label', label);
      }
    });
  }

  private getLabelText(raw: string | null | undefined, fallback = 'Value'): string {
    const base = (raw || '').trim();
    if (!base) return fallback;

    const cleaned = base
        .replace(/[_\-\s]+/g, ' ')
        .replace(/([a-z0-9])(?=[A-Z])|([A-Z])(?=[A-Z][a-z])/g, '$& ')
        .replace(/\s+/g, ' ')
        .trim();

    return cleaned
      .split(' ')
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
      .join(' ');

  }

  // fix for PrimeNG components
  private fixPrimeNGComponents(el: HTMLElement): void {
    const componentConfig: Record<string, Record<string, string>> = {
      'p-dropdown': {
        role: 'combobox',
        'aria-haspopup': 'listbox',
        'aria-expanded': 'false',
        'aria-label': 'dropdown menu',
      },
      'p-multiselect': {
        role: 'combobox',
        'aria-haspopup': 'listbox',
        'aria-expanded': 'false',
        'aria-label': 'Filter by routing type',
      },
      'p-calendar': {
        // default
      },
      'p-autocomplete': {
        // default
      },
      'p-datepicker': {
        // default
      },
    };

    // apply defaults from config map
    Object.entries(componentConfig).forEach(([cls, attrs]) => {
      if (el.classList.contains(cls)) {
        Object.entries(attrs).forEach(([attr, value]) => this.setIfMissing(el, attr, value));
      }
    });

    // radios
    if (el.classList.contains('p-radiobutton')) {
      const input = el.querySelector('input[type="radio"]') as HTMLInputElement | null;
      const nearby =
        el.getAttribute('data-label')?.trim() ||
        el.closest('.field, .form-group')?.querySelector('label')?.textContent?.trim() ||
        el.textContent?.trim() ||
        '';
      const labelText = this.getLabelText(nearby, 'Radio Option') ?? (nearby || 'Radio Option');

      if (input) {
        this.setIfMissing(input, 'aria-label', labelText);
        this.setRole(input, 'radio');
        // Keep aria-checked synced with state
        this.setIfMissing(input, 'aria-checked', input.checked ? 'true' : 'false');
        input.addEventListener('change', () => {
          input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
        });
      }
      if (!el.hasAttribute('tabindex') && !el.classList.contains('p-disabled') && !input?.disabled) {
        el.setAttribute('tabindex', '0');
      }
    }

    // checkboxes
    if (el.classList.contains('p-checkbox')) {
      const box = el.querySelector('.p-checkbox-box') as HTMLElement | null;
      const input = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
      const parentInteractive = el.closest('[role="option"], [role="row"], [role="button"], [role="link"]');
      const isNestedInInteractive = !!parentInteractive && parentInteractive !== el;

      const nearby =
        el.getAttribute('data-label')?.trim() ||
        el.closest('.field, .form-group')?.querySelector('label')?.textContent?.trim() ||
        el.textContent?.trim() ||
        '';
      const computedLabel =
        this.getLabelText(nearby, input?.getAttribute('aria-label') || 'Checkbox Option') ??
        (nearby || input?.getAttribute('aria-label') || 'Checkbox Option');

      if (box) {
        if (!isNestedInInteractive) {
          this.setRole(box, 'checkbox');
          this.setIfMissing(box, 'tabindex', '0');
          this.setIfMissing(box, 'aria-checked', input?.checked ? 'true' : 'false');
        } else {
          box.removeAttribute('role');
          box.removeAttribute('tabindex');
          box.removeAttribute('aria-checked');
        }
        this.setIfMissing(box, 'aria-label', computedLabel);
      }
    }

    // multiselect host combobox attributes (already in config), plus listbox labeling ----
    if (el.tagName === 'UL' && el.getAttribute('role') === 'listbox') {
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', 'Available options');
      }
    }

    // listbox children roles (multiselect)
    if (el.tagName === 'LI' && el.closest('ul[role="listbox"]')) {
      if (el.classList.contains('p-multiselect-item')) this.setRole(el, 'option');
      if (el.classList.contains('p-multiselect-item-group')) this.setRole(el, 'group');
    }

    // calendar input
    if (el.classList.contains('p-calendar')) {
      const input = el.querySelector('input[type="text"]') as HTMLElement | null;
      const popup = el.querySelector('.p-datepicker') as HTMLElement | null;
      if (input) {
        this.setRole(input, 'combobox');
        this.setIfMissing(input, 'aria-haspopup', 'dialog');
        this.setIfMissing(input, 'aria-expanded', 'false');
        this.setIfMissing(input, 'aria-label', 'Date picker');
        if (popup) {
          const pid = this.ensureId(popup, 'calendar-panel');
          this.setIfMissing(input, 'aria-controls', pid);
        }
      }
    }

    // autocomplete input
    if (el.classList.contains('p-autocomplete')) {
      const input = el.querySelector('input[type="text"]') as HTMLElement | null;
      if (input) {
        this.setRole(input, 'combobox');
        this.setIfMissing(input, 'aria-haspopup', 'listbox');
        this.setIfMissing(input, 'aria-expanded', 'false');
        this.setIfMissing(input, 'aria-label', 'Search suggestions');
      }
    }

    // datepicker input
    if (el.classList.contains('p-datepicker')) {
      const input = el.querySelector('input[type="text"]') as HTMLElement | null;
      if (input) {
        this.setRole(input, 'combobox');
        this.setIfMissing(input, 'aria-haspopup', 'dialog');
        this.setIfMissing(input, 'aria-expanded', 'false');
        this.setIfMissing(input, 'aria-label', 'Date picker');
      }
    }

    // combobox to listbox
    if (el.classList.contains('p-dropdown')) {
      this.setRole(el, 'combobox');
      this.setIfMissing(el, 'aria-haspopup', 'listbox');
      this.setIfMissing(el, 'aria-expanded', 'false');
      this.setIfMissing(el, 'aria-label', 'dropdown menu');
    }

    //dropdown in paginator
    if (el.classList.contains('p-dropdown') && el.classList.contains('p-paginator-rpp-options')) {
      const input = el.querySelector('input[type="text"]') as HTMLElement | null;
      const trigger = el.querySelector('.p-dropdown-trigger') as HTMLElement | null;
      const label = el.querySelector('.p-dropdown-label') as HTMLElement | null;

      if (input) {
        this.setRole(input, 'combobox');
        this.setIfMissing(input, 'aria-haspopup', 'listbox');
        this.setIfMissing(input, 'aria-expanded', 'false');
        this.setIfMissing(input, 'aria-label', 'Items per page selector');
        if (!input.hasAttribute('disabled') && el.classList.contains('p-disabled')) {
          input.setAttribute('disabled', '');
        }
      }
      this.setIfMissing(trigger, 'aria-label', 'Open items per page dropdown');
      this.setIfMissing(label, 'aria-label', 'Selected items per page');
    }

    if (el.tagName === 'BUTTON' && el.classList.contains('p-paginator-element')) {
      this.fixPaginatorButton(el);
    }

    if (
      el.tagName === 'INPUT' &&
      el.getAttribute('type') === 'text' &&
      el.getAttribute('readonly') === 'readonly'
    ) {
      this.setRole(el, 'combobox');
      this.setIfMissing(el, 'aria-haspopup', 'listbox');
      this.setIfMissing(el, 'aria-expanded', 'false');
      this.setIfMissing(el, 'aria-label', 'Selection input');
      this.setIfMissing(el, 'tabindex', '0');

      if (!el.hasAttribute('aria-controls')) {
        const isMultiSelect = !!el.closest('.p-multiselect');
        const panelSelector = isMultiSelect
          ? '.p-multiselect-panel ul[role="listbox"]'
          : '.p-dropdown-panel ul[role="listbox"], .p-autocomplete-panel ul[role="listbox"]';

        let listbox = this.document.querySelector(panelSelector) as HTMLElement | null;
        if (!listbox) listbox = this.document.querySelector('ul[role="listbox"]') as HTMLElement | null;

        if (listbox) {
          const listboxId = this.ensureId(listbox, 'listbox');
          el.setAttribute('aria-controls', listboxId);
        } else {
          const fallbackId = `listbox-fallback-${el.id || 'input'}`;
          el.setAttribute('aria-controls', fallbackId);
        }
      }
    }

    // split button menu button
    if (el.tagName === 'BUTTON' && el.classList.contains('p-splitbutton-menubutton')) {
      if (el.hasAttribute('aria-aria-haspopup')) el.removeAttribute('aria-aria-haspopup'); // typo fix
      this.setIfMissing(el, 'aria-haspopup', 'menu');
      this.setIfMissing(el, 'aria-expanded', 'false');
      if (!el.hasAttribute('aria-label') || el.getAttribute('aria-label') === 'action') {
        el.setAttribute('aria-label', 'Open split button menu');
      }
      this.setRole(el, 'button');
      this.setAriaBool(el, 'disabled', el.hasAttribute('disabled') || el.classList.contains('p-disabled'));
      if (!el.hasAttribute('tabindex') && !el.hasAttribute('disabled')) el.setAttribute('tabindex', '0');
    }

    // dataTable / table
    if (el.classList.contains('p-table') || el.classList.contains('p-datatable')) {
      const table = el.querySelector('table') as HTMLElement | null;
      if (table) this.setRole(table, 'table');
      table?.querySelectorAll('th').forEach(th => {
        if (!th.hasAttribute('scope')) th.setAttribute('scope', 'col');
      });
    }

    // input number (spinbutton)
    if (el.classList.contains('p-inputnumber-input') && el.tagName === 'INPUT') {
      const inputElement = el as HTMLInputElement;
      this.setRole(inputElement, 'spinbutton');
      this.fixInvalidAriaValues?.(inputElement);
    }

    // prime button
    if (el.classList.contains('p-button') && el.tagName === 'BUTTON') {
      const label = el.getAttribute('ng-reflect-label');
      if (label) this.setIfMissing(el, 'aria-label', label);
    }

    // custom checkbox-like button
    if (el.classList.contains('p-button-white') && el.getAttribute('role') === 'checkbox') {
      this.setIfMissing(el, 'aria-label', 'Toggle option');
    }

    // input switch
    if (el.tagName === 'P-INPUTSWITCH' || el.classList.contains('p-inputswitch')) {
      const input = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
      if (input) {
        this.setRole(input, 'switch');
        this.setIfMissing(input, 'aria-checked', input.checked ? 'true' : 'false');
        if (!input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) {
          this.setIfMissing(input, 'aria-label', 'Toggle option');
        }
        input.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Enter') {
            input.click();
            input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
            event.preventDefault();
          }
        });
      }
    }

    // DL
    if (el.tagName === 'DL') {
      el.querySelectorAll(':scope > dl').forEach(child => {
        const wrapperDD = document.createElement('dd');
        el.insertBefore(wrapperDD, child);
        wrapperDD.appendChild(child);
      });
    }

    // dialog
    if (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog') {
      this.setIfMissing(el, 'aria-modal', 'true');
      const hasLabel = el.hasAttribute('aria-label');
      const hasLabelledBy = el.hasAttribute('aria-labelledby');
      if (!hasLabel && !hasLabelledBy) {
        const heading = el.querySelector('h1, h2, h3, .p-dialog-title') as HTMLElement | null;
        if (heading) {
          const headingId = this.ensureId(heading, 'dialog-heading');
          el.setAttribute('aria-labelledby', headingId);
        } else {
          this.setIfMissing(el, 'aria-label', 'Confirmation Dialog');
        }
      }
    }
  }

  // paginator button normalizer
  private fixPaginatorButton(btn: HTMLElement): void {
    const labelMap: Record<string, string> = {
      'p-paginator-first': 'First page',
      'p-paginator-prev': 'Previous page',
      'p-paginator-next': 'Next page',
      'p-paginator-last': 'Last page',
    };

    Object.entries(labelMap).forEach(([cls, lbl]) => {
      if (btn.classList.contains(cls)) this.setIfMissing(btn, 'aria-label', lbl);
    });

    this.setRole(btn, 'button');
    this.setAriaBool(btn, 'disabled', btn.hasAttribute('disabled') || btn.classList.contains('p-disabled'));
    if (!btn.hasAttribute('tabindex') && !btn.hasAttribute('disabled')) btn.setAttribute('tabindex', '0');
  }

    private setIfMissing(el: HTMLElement | null, attr: string, value: string): void {
    if (!el) return;
    if (!el.hasAttribute(attr)) el.setAttribute(attr, value);
  }

  private setRole(el: HTMLElement | null, role: string): void {
    if (!el) return;
    if (!el.hasAttribute('role')) el.setAttribute('role', role);
  }

  private setAriaBool(el: HTMLElement | null, name: string, value: boolean): void {
    if (!el) return;
    if (!el.hasAttribute(`aria-${name}`)) el.setAttribute(`aria-${name}`, value ? 'true' : 'false');
  }

  private ensureId(el: HTMLElement, prefix: string): string {
    if (!el.id || el.id.trim() === '') {
      let id = `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
      while (document.getElementById(id)) {
        id = `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
      }
      el.id = id;
    }
    return el.id;
  }

}
